/**
 * Places ink bar — Machine Group stamps for the working Site (Wave A).
 * Map colour is preview only; membership SoT is PATCH notification_group.
 * Empty label = no Group (Unassigned is not a Group).
 */
import { useEffect, useMemo, useState } from "react";
import { html, Ico, Alert, canonicalGroupKey, sameGroupKey } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { bumpLiveZones } from "./live.js";
import { softFleetGroupsErrorOf } from "./fleet-groups-desk.js";
import { fleetZoneDevicesOf } from "./fleet-zones-desk.js";
import { deviceHomedOnSite } from "./ops-map-region-filter.js";

const INK = ["#3DE1C8", "#7FD1FF", "#FF9B5C", "#A78BFA", "#9BE37D", "#FFD166", "#FF6B8A"];

function inkTone(name, i) {
  const s = String(name || "");
  let h = 0;
  for (let n = 0; n < s.length; n++) h = (h * 31 + s.charCodeAt(n)) >>> 0;
  return INK[(h + (i || 0)) % INK.length];
}

function guessKind(row) {
  const blob = [
    row && row.display_label, row && row.map_label, row && row.hw_code_resolved,
  ].filter(Boolean).join(" ").toLowerCase();
  if (/\b(siren|strobe|horn)\b/.test(blob)) return "siren";
  if (/\b(cam|camera)\b/.test(blob)) return "camera";
  return "node";
}

export function PlacesInkBar({ toast, siteId = "", focus = false, onOpenZones, floating = false }) {
  const site = String(siteId || "").trim();
  const posture = useApi(path("get__fleet_groups_status"));
  const cards = useApi(path("get__group-cards_settings"));
  const post = posture.data || {};
  const writeOk = post.membership_write_allowed === true;
  const devices = useApi(path("get__devices"));
  const baseline = useMemo(() => {
    const pins = fleetZoneDevicesOf(devices.data).filter((d) => deviceHomedOnSite(d, site));
    return pins.map((d) => ({
      id: d.id,
      label: d.label,
      kind: guessKind({ display_label: d.label }),
      group: String(d.group || "").trim(),
      ll: d.ll,
    }));
  }, [devices.data, site]);

  const [devs, setDevs] = useState([]);
  const [pick, setPick] = useState("");
  const [draft, setDraft] = useState("");
  const [dirty, setDirty] = useState(false);
  const save_ = useAction();

  useEffect(() => {
    if (dirty) return;
    setDevs(baseline.map((d) => ({ ...d })));
  }, [baseline, dirty]);

  const names = useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (raw) => {
      const k = canonicalGroupKey(raw);
      if (!k || seen.has(k)) return;
      seen.add(k);
      out.push(k);
    };
    for (const row of ((cards.data && cards.data.items) || [])) push(row && row.group_key);
    for (const d of devs) push(d && d.group);
    return out;
  }, [devs, cards.data]);

  const ungrouped = useMemo(
    () => devs.filter((d) => !String((d && d.group) || "").trim()),
    [devs],
  );
  const members = pick ? devs.filter((d) => sameGroupKey(d.group, pick)) : [];
  const rest = pick ? devs.filter((d) => !sameGroupKey(d.group, pick)) : [];

  const move = (id, group) => {
    if (!writeOk) {
      toast && toast("Membership changes closed for this seat", "warn");
      return;
    }
    const next = !group ? "" : canonicalGroupKey(group);
    setDevs((v) => v.map((d) => (d.id === id ? { ...d, group: next } : d)));
    setDirty(true);
  };

  const saveMembership = async () => {
    if (!writeOk) {
      toast && toast("Membership changes closed for this seat", "warn");
      return;
    }
    const prevMap = new Map(baseline.map((d) => [d.id, String(d.group || "").trim()]));
    let ok = 0;
    let fail = 0;
    for (const d of devs) {
      const next = String(d.group || "").trim();
      if (prevMap.get(d.id) === next) continue;
      const done = await save_.run(
        path("patch__devices_p_profile", { pathParams: [d.id] }),
        { method: "PATCH", body: { notification_group: next } },
      );
      if (done) ok += 1;
      else fail += 1;
    }
    if (fail && !ok) {
      toast && toast(softFleetGroupsErrorOf(save_.error || "Could not save"), "bad");
      return;
    }
    setDirty(false);
    devices.reload && devices.reload();
    if (ok) bumpLiveZones({ reason: "inks-membership", site, ok, fail });
    toast && toast(
      fail ? `Saved ${ok} · ${fail} blocked` : `Group stamps saved · ${ok}`,
      fail ? "warn" : "go",
    );
  };

  const startInk = async () => {
    if (!writeOk) {
      toast && toast("Membership changes closed for this seat", "warn");
      return;
    }
    const key = canonicalGroupKey(draft);
    if (!key) {
      toast && toast("Enter an ink name first", "warn");
      return;
    }
    const known = names.some((n) => sameGroupKey(n, key));
    if (!known) {
      const done = await save_.run(
        path("put__group-cards_p_settings", { pathParams: [key] }),
        { method: "PUT", body: {} },
      );
      if (!done) {
        toast && toast(softFleetGroupsErrorOf(save_.error || "Could not save the ink"), "bad");
        return;
      }
      cards.reload && cards.reload();
      toast && toast("Ink saved", "go");
    } else {
      toast && toast("Ink selected", "go");
    }
    setPick(key);
    setDraft("");
  };

  return html`<section className="places-ink" data-focus=${focus ? "1" : "0"}
    data-dirty=${dirty ? "1" : "0"} data-float=${floating ? "1" : "0"}
    data-sheet=${focus || pick ? "1" : "0"} data-cmc-ink-site=${site}>
    ${save_.envelope
      ? html`<p className="note" data-cmc-last-action="1">Last action · ${envelopeSummary(save_.envelope) || "—"}</p>`
      : null}
    <div className="places-ink__rail" role="tablist" aria-label="Machine Group inks">
      <span className="places-ink__label">Inks</span>
      ${names.map((name, i) => {
        const count = devs.filter((d) => sameGroupKey(d.group, name)).length;
        return html`<button key=${name} type="button" role="tab"
        className="places-ink__chip" data-on=${sameGroupKey(pick, name) ? "1" : "0"}
        style=${{ "--ink": inkTone(name, i) }}
        aria-selected=${sameGroupKey(pick, name) ? "true" : "false"}
        aria-label=${`${name}, ${count} machines`}
        onClick=${() => setPick(sameGroupKey(pick, name) ? "" : name)}>
        <i></i><b>${name}</b>
        <em>${count}</em>
      </button>`;
      })}
      <button type="button" className="places-ink__chip places-ink__chip--void"
        data-on=${!pick && focus ? "1" : "0"}
        onClick=${() => setPick("")}
        title="Machines with no Group">
        <b>No group</b><em>${ungrouped.length}</em>
      </button>
      ${writeOk ? html`<div className="places-ink__new">
        <input value=${draft} placeholder="New ink" aria-label="New Machine Group ink"
          onInput=${(e) => setDraft(e.target.value)}
          onKeyDown=${(e) => { if (e.key === "Enter") startInk(); }}/>
        <button type="button" className="btn btn--ghost btn--sm" onClick=${startInk}>Add</button>
      </div>` : null}
    </div>

    ${focus || pick ? html`<div className="places-ink__dock" role="dialog" aria-label="Ink stamp dock">
      ${floating ? html`<header className="places-ink__sheet-head">
        <div><span>Stamp</span><b>${pick || "Pick an ink"}</b></div>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => { setPick(""); if (onOpenZones) onOpenZones(); }}>
          <${Ico} d="close" w=${14}/> Close
        </button>
      </header>` : null}
      ${devices.error ? html`<${Alert} tone="warn" title="Could not load machines"
        say=${softFleetGroupsErrorOf(devices.error)}/>` : null}
      ${!pick ? html`<p className="places-ink__hint">
        Select an ink to stamp machines on this work. Empty label means no Group.
        ${onOpenZones ? html` · <button type="button" className="btn btn--text btn--sm" onClick=${onOpenZones}>Back to map</button>` : null}
      </p>` : html`
        <header className="places-ink__dock-head">
          <div>
            <span>Machine Group</span>
            <b style=${{ color: inkTone(pick) }}>${pick}</b>
            <small>${members.length} on this work · map colour is preview only</small>
          </div>
          <div className="btn-row">
            ${dirty ? html`<button type="button" className="btn btn--go btn--sm"
              disabled=${save_.busy} onClick=${saveMembership}>Save stamps</button>
              <button type="button" className="btn btn--ghost btn--sm"
                onClick=${() => { setDirty(false); setDevs(baseline.map((d) => ({ ...d }))); }}>Discard</button>` : null}
          </div>
        </header>
        <div className="places-ink__cols">
          <div>
            <h4>In this ink</h4>
            ${!members.length ? html`<p className="note">Saved. Stamp a machine when one is on this work.</p>` : null}
            ${members.map((d) => writeOk ? html`<button key=${d.id} type="button" className="places-ink__row"
              onClick=${() => move(d.id, "")}>
              <${Ico} d="chip" w=${12}/><b>${d.label}</b><em>Remove</em>
            </button>` : html`<div key=${d.id} className="places-ink__row">
              <${Ico} d="chip" w=${12}/><b>${d.label}</b>
            </div>`)}
          </div>
          <div>
            <h4>Rest of this work</h4>
            ${rest.map((d) => writeOk ? html`<button key=${d.id} type="button" className="places-ink__row"
              onClick=${() => move(d.id, pick)}>
              <${Ico} d="chip" w=${12}/><b>${d.label}</b>
              <em>${d.group || "no group"}</em>
            </button>` : html`<div key=${d.id} className="places-ink__row">
              <${Ico} d="chip" w=${12}/><b>${d.label}</b>
              <em>${d.group || "no group"}</em>
            </div>`)}
          </div>
        </div>
      `}
    </div>` : null}
  </section>`;
}
